import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchTrackingRecords,
  updateFilters,
  updatePagination,
  fetchTrackingStats,
  resetError
} from '../liveUrlTrackingActions';
import LiveUrlFilters from './LiveUrlFilters';
import LiveUrlDataTable from './LiveUrlDataTable';
import LiveUrlPagination from './LiveUrlPagination';
import { getDirectoriesOptions } from '../../User/actions';
import {
  normalizeRootFolderOptions
} from '../liveUrlTrackingConstants';
import { getTodayIsoDate } from './liveUrlFilterDateUtils';
import './LiveUrlTrackingList.css';

const LiveUrlTrackingList = () => {
  const dispatch = useDispatch();
  const directoriesList = useSelector((state) => state.UserReducer?.directoriesList || []);
  const rootFolderOptions = useMemo(
    () => normalizeRootFolderOptions(directoriesList),
    [directoriesList]
  );

  const trackingState = useSelector((state) => state.liveUrlTracking || {
    records: [],
    loading: false,
    error: null,
    pagination: { currentPage: 1, totalPages: 1, totalRecords: 0, pageSize: 20 },
    filters: {
      rootFolder: [],
      pushedBy: '',
      dateFrom: null,
      dateTo: null,
      fileType: [],
      sortBy: 'pushedAt',
      sortOrder: 'desc'
    },
    statistics: null,
    statsLoading: false,
    statsError: null
  });

  useEffect(() => {
    const today = getTodayIsoDate();
    dispatch(updateFilters({
      dateFrom: today,
      dateTo: today,
      rootFolder: [],
      pushedBy: '',
      fileType: []
    }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getDirectoriesOptions({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTrackingRecords());
    dispatch(fetchTrackingStats());
  }, [
    dispatch,
    trackingState.filters.sortBy,
    trackingState.filters.sortOrder,
    trackingState.pagination.currentPage,
    trackingState.pagination.pageSize
  ]);

  const handleFilterChange = (newFilters) => {
    dispatch(updateFilters(newFilters));

    if (trackingState.pagination.currentPage !== 1) {
      dispatch(updatePagination({ currentPage: 1 }));
    } else {
      dispatch(fetchTrackingRecords());
      dispatch(fetchTrackingStats());
    }
  };

  const handleSortChange = (sortBy, sortOrder) => {
    dispatch(updateFilters({ sortBy, sortOrder }));
  };

  const handlePageChange = (newPage) => {
    dispatch(updatePagination({ currentPage: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    dispatch(updatePagination({
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleRetry = () => {
    dispatch(resetError());
    dispatch(fetchTrackingRecords());
    dispatch(fetchTrackingStats());
  };

  return (
    <div className="live-url-tracking-list" data-testid="live-url-tracking-list">
      <div className="live-url-tracking-header">
        <div className="live-url-tracking-header-main">
          <div className="live-url-tracking-header-text">
            <h2>Live URL Tracking</h2>
            <p className="live-url-tracking-description">
              Monitor and manage live URLs for pushed media files
            </p>
          </div>

          {trackingState.statistics && !trackingState.statsLoading && (
            <div className="live-url-stats-summary" data-testid="stats-summary">
              <div className="stat-item">
                <span className="stat-label">Total Records:</span>
                <span className="stat-value">{trackingState.statistics.totalRecords}</span>
              </div>
              {trackingState.statistics.recentActivity && (
                <>
                  <div className="stat-item">
                    <span className="stat-label">Pushes Today:</span>
                    <span className="stat-value">{trackingState.statistics.recentActivity.pushesToday}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">This Week:</span>
                    <span className="stat-value">{trackingState.statistics.recentActivity.pushesThisWeek}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <LiveUrlFilters
        filters={trackingState.filters}
        onFilterChange={handleFilterChange}
        rootFolderOptions={rootFolderOptions}
        isLoading={trackingState.loading}
      />

      <LiveUrlDataTable
        records={trackingState.records}
        loading={trackingState.loading}
        error={trackingState.error}
        onSort={handleSortChange}
        onRetry={handleRetry}
        currentSort={{
          field: trackingState.filters.sortBy,
          order: trackingState.filters.sortOrder
        }}
      />

      <LiveUrlPagination
        pagination={trackingState.pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={trackingState.loading}
      />
    </div>
  );
};

export default LiveUrlTrackingList;
